import React, { ComponentPropsWithoutRef, FC, ReactNode, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { ProductMenu } from "../../../products/ProductMenu";
import IdPill from "../../../../components/IdPill/IdPill";
import { initialsFromName } from "../../../../utils";
import { brandLogoSources } from "../../../../utils/brandLogo";
import { IconDownload, IconEye, IconLink, IconProduct, IconRefresh } from "../../../../utils/icons";
import { scoreCls } from "../../../../components/PdpScoreStatus/PdpScoreStatus";
import { PdpScoreReportDialog } from "../../../../components/PdpScoreStatus/PdpScoreReportDialog";
import { RunningBadge } from "../../../../components/RunningBadge";
import { downloadPdpScorePdf } from "../../../../components/PdpAudit/reportFiles";
import { TechChip } from "../../../../components/videoSection/VideoCard";

interface BrandLike {
    id?: number | string;
    name?: string;
    logo?: string;
    cover_image?: string;
    rmn?: boolean;
}

// An active PDP URL row: belongs to the product AND one brand.
interface PdpUrlLike {
    id: number;
    brand_id: number | string;
    pdp_url: string;
    retailer?: string | null;
    // Newest ready score, or null when never scored.
    latest_score?: { id: number; overall_score: number | null; scored_at: string | null } | null;
}

interface ContentCardImageProps extends ComponentPropsWithoutRef<"div"> {
    image?: JSX.Element | string;
    actions?: {
        onEdit?: () => void;
        onDelete?: () => void;
    };
    brands?: BrandLike[] | null;
    productId?: string | number;
    videoCount?: number;
    // The product's active PDP URLs (list endpoints attach pdp_urls); shown
    // under the brand each one belongs to.
    pdpUrls?: PdpUrlLike[] | null;
    title?: string;
    description?: string;
    onBrandClick?: (brandId: number | string) => void;
    // Internal-only: "Run PDP Analysis" icon on each PDP URL line. Omitted
    // for clients, who never start audits from the card.
    onRunPdp?: (pdpUrl: PdpUrlLike) => void;
    // Product-level flag from the list endpoint: a report is in flight on
    // one of this product's pages, so every run icon disables.
    pdpReportRunning?: boolean;
    // Internal-only strip under the PDP URLs (Consumer Voice score / report
    // and the Run buttons). Clicks inside it never open the product.
    intel?: ReactNode;
    children?: ReactNode;
}

const PLATE_BG = "#FFFFFF";

const BrandAvatar: FC<{ name?: string; logo?: string; cover_image?: string }> = ({
    name,
    logo,
    cover_image
}) => {
    const sources = useMemo(
        () => brandLogoSources({ logo, cover_image, name }),
        [logo, cover_image, name]
    );
    const [sourceIndex, setSourceIndex] = useState(0);

    useEffect(() => setSourceIndex(0), [sources]);

    const src = sources[sourceIndex];

    return (
        <span
            className="w-6 h-6 shrink-0 inline-flex items-center justify-center overflow-hidden rounded-md bg-surface-2 text-accent-text font-bold"
            style={{ fontSize: 10 }}
        >
            {src ? (
                <img
                    src={src}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-contain"
                    onError={() => setSourceIndex((i) => i + 1)}
                />
            ) : (
                initialsFromName(name || "")
            )}
        </span>
    );
};

// "artika.com/products/sunnyvale-…" — the scheme and www. add nothing on a card.
const pdpLabel = (url: string) => url.replace(/^https?:\/\//i, "").replace(/^www\./i, "");

const clamp = (lines: number) => ({
    display: "-webkit-box" as const,
    WebkitLineClamp: lines,
    WebkitBoxOrient: "vertical" as const,
    overflow: "hidden" as const
});

export function ProductCard({
    children,
    image,
    brands,
    productId,
    videoCount,
    pdpUrls,
    title,
    description,
    onBrandClick,
    onRunPdp,
    pdpReportRunning,
    intel,
    ...props
}: ContentCardImageProps) {
    const list = Array.isArray(brands) ? brands.filter(Boolean) : [];
    const supplier = list.find((b) => !b.rmn) || list[0];
    const retailer = list.find((b) => b.rmn && b !== supplier);
    const videos = Number(videoCount) || 0;
    const [viewingReport, setViewingReport] = useState<{ pdpUrlId: number; scoreId: number; pdpUrl: string } | null>(
        null
    );
    const urlsByBrand = useMemo(() => {
        const map: Record<string, PdpUrlLike[]> = {};
        (Array.isArray(pdpUrls) ? pdpUrls : []).forEach((u) => {
            if (!u?.pdp_url) return;
            (map[String(u.brand_id)] = map[String(u.brand_id)] || []).push(u);
        });
        return map;
    }, [pdpUrls]);
    const source = typeof image === "string" ? image : "";

    const brandRow = (brand: BrandLike) => {
        const clickable = !!onBrandClick && brand.id != null;
        return (
            <span
                className={classNames("inline-flex items-center gap-2 min-w-0 text-text-1", {
                    "cursor-pointer hover:text-accent-text": clickable
                })}
                style={{ fontSize: 12.5 }}
                onClick={
                    clickable
                        ? (e) => {
                              e.stopPropagation();
                              onBrandClick!(brand.id!);
                          }
                        : undefined
                }
            >
                <BrandAvatar name={brand.name} logo={brand.logo} cover_image={brand.cover_image} />
                <span className="truncate">{brand.name}</span>
            </span>
        );
    };

    // One PDP URL line: latest score, view / download the last report, the
    // run icon (internal), then the link. The report actions need a stored
    // score; without one they are omitted and the score reads "—". The run
    // icon has no such dependency — an unscored page is exactly the one you
    // want to audit.
    const iconBtnCls =
        "inline-flex items-center justify-center w-[20px] h-[20px] rounded-md text-text-2 hover:text-accent-text hover:bg-surface-2 transition-colors shrink-0";
    const urlLine = (u: PdpUrlLike, more: number, prefix?: string | null, indent = 32) => {
        const score = u.latest_score || null;
        const value = score ? score.overall_score : null;
        const canOpen = !!(score && productId);
        return (
            <div className="flex items-center gap-[6px] min-w-0" style={{ fontSize: 11.5, paddingLeft: indent }}>
                <span
                    className={classNames(
                        "inline-flex items-center justify-center min-w-[26px] h-[20px] px-[6px] rounded-md bg-surface-2 border border-border-token font-bold tabular-nums shrink-0",
                        scoreCls(value)
                    )}
                    title={score && score.scored_at ? `Last scored ${new Date(score.scored_at).toLocaleDateString()}` : "Not scored yet"}
                >
                    {value == null ? "—" : value}
                </span>
                {canOpen && (
                    <>
                        <button
                            type="button"
                            className={iconBtnCls}
                            title="View last report"
                            onClick={(e) => {
                                e.stopPropagation();
                                setViewingReport({ pdpUrlId: u.id, scoreId: score!.id, pdpUrl: u.pdp_url });
                            }}
                        >
                            <IconEye width={14} height={14} stroke="currentColor" />
                        </button>
                        <button
                            type="button"
                            className={iconBtnCls}
                            title="Download last report"
                            onClick={(e) => {
                                e.stopPropagation();
                                downloadPdpScorePdf({
                                    productId: Number(productId),
                                    pdpUrlId: u.id,
                                    scoreId: score!.id,
                                    url: u.pdp_url
                                });
                            }}
                        >
                            <IconDownload width={13} height={13} stroke="currentColor" />
                        </button>
                    </>
                )}
                {/* While a report runs the icon becomes the same spinner the
                    BI status badge shows, so the card reads "in progress"
                    rather than just a greyed-out button. The flag is
                    product-level, so every URL line shows it. */}
                {onRunPdp && pdpReportRunning && (
                    <RunningBadge
                        label="Analyzing"
                        title="A PDP analysis is running for this product — the score updates when it finishes."
                    />
                )}
                {onRunPdp && !pdpReportRunning && (
                    <button
                        type="button"
                        className={iconBtnCls}
                        title="Run PDP Analysis on this page"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRunPdp(u);
                        }}
                    >
                        <IconRefresh size={13} />
                    </button>
                )}
                <a
                    href={u.pdp_url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 min-w-0 text-accent-text hover:underline"
                    title={u.pdp_url}
                >
                    <span className="inline-flex -rotate-45 shrink-0">
                        <IconLink width={10} height={10} stroke="currentColor" strokeWidth={2.4} />
                    </span>
                    <span className="truncate">
                        {prefix ? `${prefix}: ` : ""}
                        {pdpLabel(u.pdp_url)}
                    </span>
                    {more > 0 && <span className="shrink-0 text-text-2">+{more}</span>}
                </a>
            </div>
        );
    };

    // Under each brand: that brand's PDP URL for this product (a PDP URL is
    // per product + brand), or an explicit "none" so absence is visible too.
    const brandBlock = (brand: BrandLike) => {
        const urls = urlsByBrand[String(brand.id)] || [];
        return (
            <div className="flex flex-col min-w-0" style={{ gap: 4 }}>
                {brandRow(brand)}
                {urls.length ? (
                    urlLine(urls[0], urls.length - 1)
                ) : (
                    <span className="text-text-2" style={{ fontSize: 11.5, paddingLeft: 32 }}>
                        No PDP URL
                    </span>
                )}
            </div>
        );
    };

    return (
        <div
            className="bg-surface border border-border-token rounded-xl overflow-hidden flex flex-col h-full w-full box-border cursor-pointer transition-colors duration-150 hover:border-accent"
            style={{ boxShadow: "var(--sh-1)" }}
            title={title}
            {...props}
        >
            <div
                className="relative shrink-0 h-[158px] flex items-center justify-center"
                style={{ background: PLATE_BG }}
            >
                {source ? (
                    <img src={source} alt="" loading="lazy" className="w-full h-full object-contain" />
                ) : (
                    <IconProduct width={40} height={40} />
                )}
                {/* Same chip, same corner as the shortlist cards' counts. */}
                <span className="absolute bottom-2 left-2">
                    <TechChip>
                        {videos} {videos === 1 ? "video" : "videos"}
                    </TechChip>
                </span>
            </div>

            <div className="flex flex-col flex-1 min-h-0" style={{ gap: 12, padding: "13px 15px 15px" }}>
                <div className="flex items-start gap-2.5">
                    <span className="flex-1 min-w-0 flex items-center" onClick={(e) => e.stopPropagation()}>
                        {productId && <IdPill value={productId} />}
                    </span>
                    <span className="flex items-center shrink-0" onClick={(e) => e.stopPropagation()}>
                        <ProductMenu {...props} />
                    </span>
                </div>

                <div>
                    {title && (
                        <div className="font-bold text-text-1" style={{ fontSize: 15.5, lineHeight: 1.3, ...clamp(2) }}>
                            {title}
                        </div>
                    )}
                    {description && (
                        <div
                            className="text-text-2"
                            style={{ marginTop: 5, fontSize: 12.5, lineHeight: 1.5, ...clamp(2) }}
                        >
                            {description}
                        </div>
                    )}
                </div>

                {children}

                <div
                    className="flex flex-col border-t border-border-token mt-auto"
                    style={{ gap: 9, paddingTop: 11 }}
                >
                    {supplier && brandBlock(supplier)}
                    {retailer && brandBlock(retailer)}
                    {/* A URL on a brand the product isn't linked to still shows, never silently drops. */}
                    {Object.entries(urlsByBrand)
                        .filter(([id]) => id !== String(supplier?.id) && id !== String(retailer?.id))
                        .map(([id, urls]) => (
                            <React.Fragment key={id}>
                                {urlLine(urls[0], urls.length - 1, urls[0].retailer, 0)}
                            </React.Fragment>
                        ))}
                    {intel && (
                        <div
                            className="border-t border-border-token"
                            style={{ paddingTop: 9 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {intel}
                        </div>
                    )}
                </div>
            </div>
            {viewingReport && productId && (
                // MUI portals the dialog, but React events still bubble to the
                // card's onClick — keep clicks inside it from opening the product.
                <span onClick={(e) => e.stopPropagation()}>
                    <PdpScoreReportDialog
                        productId={Number(productId)}
                        pdpUrlId={viewingReport.pdpUrlId}
                        scoreId={viewingReport.scoreId}
                        pdpUrl={viewingReport.pdpUrl}
                        onClose={() => setViewingReport(null)}
                    />
                </span>
            )}
        </div>
    );
}

ProductCard.defaultProps = {
    image: ""
};
